import { EXPIRY_STATE_LABELS, EXPIRY_WARNING_DAYS, LOCATION_TYPE_LABELS, daysUntil, expiryState, type ExpiryState, type LocationType, type StockLine, type StockLot } from '@wms/domain';
import { Fragment, useEffect, useState } from 'react';
import { api } from '../api';
import { fmt } from '../format';
import { useRoute } from '../router';
import { useSession } from '../session';
import { inFilter } from '../stock';
import { Empty, ErrorBanner, SellerChip, Spinner, useLoad } from '../ui';
import { Icon } from '../ui/icons';
import { useSort } from '../ui/sort';

const FILTERS = [
  { key: 'all', label: 'Tümü' },
  { key: 'tracked', label: 'Lot / SKT takipli' },
  { key: 'soon', label: "SKT'si yaklaşan" },
  { key: 'expired', label: 'Süresi geçen' },
] as const;
type Filter = (typeof FILTERS)[number]['key'];

const TONE: Readonly<Record<ExpiryState, string>> = { expired: 'bad', short_life: 'bad', soon: 'warn', ok: 'ok' };
const today = () => fmt.today();

/** "SKT 12.11.2026 · 30 gün" with the colour of its state. */
export function ExpiryChip({ date, minShelfLifeDays }: { date: string; minShelfLifeDays: number | null }) {
  const state = expiryState(date, today(), minShelfLifeDays);
  const days = daysUntil(date, today());
  return (
    <span className={`chip ${TONE[state]}`} title={EXPIRY_STATE_LABELS[state]}>
      {fmt.date(date)} · {days < 0 ? `${-days} gün geçti` : days === 0 ? 'bugün' : `${days} gün`}
    </span>
  );
}

/**
 * fiziksel / rezerve / kullanılabilir stok per product with the nearest expiry date; a row opens
 * its lots (FEFO order) with their dates and places. Sellers see only their own products (RLS).
 */
export function Stock() {
  const { isOperator } = useSession();
  const list = useLoad(() => api<StockLine[]>('GET', '/api/stock'), [], 30_000);
  const [search, setSearch] = useState('');
  // links from the start screens open on the expiring products: #/stok?skt=yakin
  const linked: Filter = useRoute().query.get('skt') === 'yakin' ? 'soon' : 'all';
  const [filter, setFilter] = useState<Filter>(linked);
  useEffect(() => setFilter(linked), [linked]);
  const [open, setOpen] = useState<string | null>(null);
  const sort = useSort<StockLine>('stock', {
    ...(isOperator ? { seller: { label: 'Satıcı', value: (r) => r.tenantCode } } : {}),
    product: { label: 'Ürün', value: (r) => r.sku },
    physical: { label: 'Fiziksel', value: (r) => r.physical, desc: true },
    reserved: { label: 'Rezerve', value: (r) => r.reserved, desc: true },
    available: { label: 'Kullanılabilir', value: (r) => r.available, desc: true },
    inbound: { label: 'Kabulde / yolda', value: (r) => r.inbound, desc: true },
    blocked: { label: 'Bloke', value: (r) => r.blocked, desc: true },
    expiry: { label: 'En yakın SKT', value: (r) => r.nextExpiry },
  });
  const term = search.trim().toLocaleUpperCase('tr');
  const rows = sort.apply(
    (list.data ?? []).filter((r) => {
      if (term && !`${r.sku} ${r.name}`.toLocaleUpperCase('tr').includes(term)) return false;
      return inFilter(r, filter);
    }),
  );
  const total = (pick: (r: StockLine) => number) => fmt.n(rows.reduce((n, r) => n + pick(r), 0));
  const columns = 8 + (isOperator ? 2 : 0);

  return (
    <>
      <div className="row">
        {FILTERS.map((f) => (
          <button key={f.key} className={`chip select${filter === f.key ? ' on' : ''}`} onClick={() => setFilter(f.key)}>
            {f.label}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        {sort.picker}
        <input className="input" style={{ maxWidth: 260 }} placeholder="SKU veya ürün adı" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <span className="muted" title={`Lotları ve son kullanma tarihlerini görmek için satıra dokunun; SKT'si ${EXPIRY_WARNING_DAYS} gün içinde dolan veya raf ömrü yetmeyen lotlar sarı / kırmızı görünür.`}>
        {rows.length} ürün · {total((r) => r.physical)} fiziksel · {total((r) => r.available)} kullanılabilir · ayrıntı için satıra dokunun
      </span>
      <section className="card">
        {list.error && <ErrorBanner message={list.error} onRetry={() => void list.reload()} />}
        {!list.data ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <Empty title={filter === 'all' ? 'Stok kaydı yok' : 'Bu filtrede ürün yok'} />
        ) : (
          <div className="table-wrap">
            <table className="t stack-sm">
              <thead>
                <tr>
                  {isOperator && sort.th('seller')}
                  {sort.th('product')}
                  {sort.th('physical', 'num')}
                  {sort.th('reserved', 'num hide-sm')}
                  {sort.th('available', 'num')}
                  {sort.th('inbound', 'num hide-sm')}
                  {sort.th('blocked', 'num hide-sm')}
                  {sort.th('expiry')}
                  <th />
                  {isOperator && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const expanded = open === r.productId;
                  return (
                    <Fragment key={r.productId}>
                      <tr className="clickable" onClick={() => setOpen(expanded ? null : r.productId)} aria-expanded={expanded}>
                        {isOperator && (
                          <td>
                            <SellerChip code={r.tenantCode} />
                          </td>
                        )}
                        <td>
                          <b className="mono">{r.sku}</b>
                          <div className="muted" style={{ fontSize: 13 }}>
                            {r.name}
                          </div>
                        </td>
                        <td className="num" data-label="Fiziksel">{fmt.n(r.physical)}</td>
                        <td className="num hide-sm">{fmt.n(r.reserved)}</td>
                        <td className="num" data-label="Kullanılabilir">
                          <b>{fmt.n(r.available)}</b>
                        </td>
                        <td className="num hide-sm">{r.inbound ? fmt.n(r.inbound) : '—'}</td>
                        <td className="num hide-sm">{r.blocked ? <span className="chip warn">{fmt.n(r.blocked)}</span> : '—'}</td>
                        <td>
                          {r.expiredQty > 0 && <span className="chip bad" style={{ marginRight: 4 }}>{fmt.n(r.expiredQty)} adet SKT geçti</span>}
                          {r.nextExpiry ? <ExpiryChip date={r.nextExpiry} minShelfLifeDays={r.minShelfLifeDays} /> : r.trackExpiry ? <span className="muted">—</span> : <span className="muted">takip yok</span>}
                        </td>
                        <td>
                          <Icon name={expanded ? 'x' : 'list'} size={16} />
                        </td>
                        {isOperator && (
                          <td onClick={(e) => e.stopPropagation()}>
                            {r.physical > 0 && (
                              <a className="btn sm ghost" href={`#/harita?q=${encodeURIComponent(r.sku)}`} title="Bu ürünün bulunduğu gözleri haritada göster" aria-label="Haritada göster">
                                <Icon name="map" size={16} />
                              </a>
                            )}
                          </td>
                        )}
                      </tr>
                      {expanded && (
                        <tr className="sub-row">
                          <td colSpan={columns}>
                            <Lots productId={r.productId} minShelfLifeDays={r.minShelfLifeDays} operator={isOperator} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

/** A product's lots, first to expire first; `stacked`: on a phone each lot is a short line with its numbers named. */
export function Lots({ productId, minShelfLifeDays, operator, stacked = false }: { productId: string; minShelfLifeDays: number | null; operator: boolean; stacked?: boolean }) {
  const lots = useLoad(() => api<StockLot[]>('GET', `/api/stock/lots?productId=${productId}`), [productId]);
  // the names only where the rows stack (inside the Stok table they would show in its stacked rows)
  const named = (label: string) => (stacked ? label : undefined);
  if (lots.error) return <ErrorBanner message={lots.error} onRetry={() => void lots.reload()} />;
  if (!lots.data) return <Spinner />;
  if (lots.data.length === 0) return <span className="muted">Depoda bu ürün yok.</span>;
  return (
    <table className={`t lots${stacked ? ' stack-sm' : ''}`}>
      <thead>
        <tr>
          <th>Lot</th>
          <th>Son kullanma</th>
          <th>Depo</th>
          <th className="num">Fiziksel</th>
          <th className="num">Rezerve</th>
          <th className="num">Kullanılabilir</th>
          <th>{operator ? 'Göz / alan' : 'Nerede'}</th>
          <th>İlk kabul</th>
        </tr>
      </thead>
      <tbody>
        {lots.data.map((l, i) => (
          <tr key={`${l.lotNo}-${l.expiryDate}-${l.warehouseCode}-${i}`}>
            <td className="mono" data-label={named('Lot')}>{l.lotNo ?? '—'}</td>
            <td>{l.expiryDate ? <ExpiryChip date={l.expiryDate} minShelfLifeDays={minShelfLifeDays} /> : '—'}</td>
            <td data-label={named('Depo')}>{l.warehouseCode}</td>
            <td className="num" data-label={named('Fiziksel')}>{fmt.n(l.physical)}</td>
            <td className="num" data-label={named('Rezerve')}>{fmt.n(l.reserved)}</td>
            <td className="num" data-label={named('Kullanılabilir')}>{fmt.n(l.available)}</td>
            <td>
              {l.places.map((p, j) => (
                <span key={j} className="chip" style={{ marginRight: 4 }} title={LOCATION_TYPE_LABELS[p.type as LocationType] ?? p.type}>
                  {operator ? `${p.code} · ${fmt.n(p.qty)}` : `${LOCATION_TYPE_LABELS[p.type as LocationType] ?? p.type} · ${fmt.n(p.qty)}`}
                </span>
              ))}
            </td>
            <td className="muted" data-label={named('İlk kabul')}>{fmt.date(l.firstReceivedAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * One product's stock on its page (a seller's Ürünler ve stok): kullanılabilir / rezerve / kabulde /
 * bloke / fiziksel, the nearest expiry and the lots behind them, as the Stok screen's row opens them.
 */
export function StockCard({ productId }: { productId: string }) {
  const { isOperator } = useSession();
  const found = useLoad(() => api<StockLine[]>('GET', `/api/stock?productId=${productId}`), [productId]);
  const s = found.data?.[0];
  const figure = (label: string, value: number, tone = '') => (
    <div className={`stock-figure${tone ? ` ${tone}` : ''}`}>
      <b>{fmt.n(value)}</b>
      <span>{label}</span>
    </div>
  );
  return (
    <section className="card">
      <div className="card-h">
        <h2>Stok</h2>
        {s && (
          <span className="row" style={{ gap: 4 }}>
            {s.expiredQty > 0 && <span className="chip bad">{fmt.n(s.expiredQty)} adet SKT geçti</span>}
            {s.nextExpiry && (
              <>
                <span className="sub">En yakın SKT</span>
                <ExpiryChip date={s.nextExpiry} minShelfLifeDays={s.minShelfLifeDays} />
              </>
            )}
          </span>
        )}
      </div>
      <div className="card-b stack">
        {found.error && <ErrorBanner message={found.error} onRetry={() => void found.reload()} />}
        {!found.data ? (
          <Spinner />
        ) : !s ? (
          <span className="muted">Depoda bu ürün yok.</span>
        ) : (
          <>
            <div className="stock-figures">
              {figure('Kullanılabilir', s.available, 'main')}
              {figure('Rezerve', s.reserved)}
              {figure('Kabulde / yolda', s.inbound)}
              {s.blocked > 0 && figure('Bloke', s.blocked, 'warn')}
              {figure('Fiziksel', s.physical)}
            </div>
            {s.physical > 0 ? (
              <div className="table-wrap">
                <Lots productId={productId} minShelfLifeDays={s.minShelfLifeDays} operator={isOperator} stacked />
              </div>
            ) : (
              !isOperator && (
                <span className="muted">
                  Depoda bu ürün yok. <a href="#/asn">Depoya gönderi</a> planlayın.
                </span>
              )
            )}
          </>
        )}
      </div>
    </section>
  );
}
